import { useState, useEffect, useCallback, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface RegistryProject {
  registry_id: string;
  name: string;
  developer: string;
  type: string;
  methodology: string;
  location: string;
  status: string;
  credits_issued: number;
  credits_retired: number;
  vintage_year?: number;
  description?: string;
  price?: number;
  registration_date?: string;
  verification_date?: string;
}

// Immediate mock data for instant UI response
const INSTANT_MOCK_DATA: RegistryProject[] = [
  {
    registry_id: "VCS-001",
    name: "Amazon Rainforest Conservation Project",
    developer: "Tropical Forest Initiative",
    type: "Forest Conservation",
    methodology: "REDD+ VM0007",
    location: "Brazil, Amazon Basin",
    status: "verified",
    credits_issued: 150000,
    credits_retired: 45000,
    vintage_year: 2023,
    description: "Large-scale forest conservation project protecting 50,000 hectares of primary rainforest.",
    registration_date: "2023-01-15",
    verification_date: "2023-06-20"
  },
  {
    registry_id: "GS-002", 
    name: "Wind Energy Development",
    developer: "GreenWind Corp",
    type: "Renewable Energy",
    methodology: "ACM0002",
    location: "India, Gujarat",
    status: "verified",
    credits_issued: 80000,
    credits_retired: 20000,
    vintage_year: 2023,
    description: "50MW wind farm providing clean energy to the national grid.",
    registration_date: "2023-02-10",
    verification_date: "2023-07-15"
  },
  {
    registry_id: "VCS-003",
    name: "Mangrove Restoration Initiative", 
    developer: "Coastal Protection Alliance",
    type: "Marine Conservation",
    methodology: "VM0033",
    location: "Philippines, Luzon",
    status: "registered",
    credits_issued: 25000,
    credits_retired: 5000,
    vintage_year: 2024,
    description: "Restoration of 2,000 hectares of mangrove ecosystems.",
    registration_date: "2024-01-08",
    verification_date: "2024-03-12"
  },
  {
    registry_id: "CDM-004",
    name: "Solar Power Installation",
    developer: "SolarTech Solutions",
    type: "Renewable Energy",
    methodology: "AMS-I.D",
    location: "Morocco, Ouarzazate",
    status: "verified",
    credits_issued: 120000,
    credits_retired: 30000,
    vintage_year: 2023,
    description: "200MW concentrated solar power plant in the Sahara Desert.",
    registration_date: "2023-03-22",
    verification_date: "2023-08-10"
  },
  {
    registry_id: "VCS-005",
    name: "Biogas from Agricultural Waste",
    developer: "Rural Energy Co-op",
    type: "Waste Management",
    methodology: "VM0024",
    location: "Kenya, Central Province",
    status: "verified",
    credits_issued: 35000,
    credits_retired: 8000,
    vintage_year: 2023,
    description: "Converting agricultural waste to clean energy for rural communities.",
    registration_date: "2023-04-05",
    verification_date: "2023-09-15"
  },
  {
    registry_id: "GS-006",
    name: "Efficient Cookstoves Program",
    developer: "Clean Air Foundation",
    type: "Energy Efficiency",
    methodology: "GS-TPDDTEC",
    location: "Uganda, Northern Region",
    status: "registered",
    credits_issued: 45000,
    credits_retired: 12000,
    vintage_year: 2024,
    description: "Distribution of improved cookstoves to reduce deforestation and emissions.",
    registration_date: "2024-02-14",
    verification_date: "2024-05-20"
  }
];

const PROJECTS_PER_PAGE = 20;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const useOptimisticRegistry = () => {
  const [projects, setProjects] = useState<RegistryProject[]>([]);
  const [allProjects, setAllProjects] = useState<RegistryProject[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isBackgroundLoading, setIsBackgroundLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const { toast } = useToast();

  // Immediately show mock data for instant UI response
  useEffect(() => {
    console.log('🚀 OptimisticRegistry: Showing instant mock data');
    setProjects(INSTANT_MOCK_DATA);
    setAllProjects(INSTANT_MOCK_DATA);
    setIsInitialLoading(false);
    
    // Start background data fetch
    fetchRealData();
  }, []);

  // Filter and paginate projects in memory
  const filteredProjects = useMemo(() => {
    let filtered = allProjects;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(project => 
        project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.developer.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        project.type.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply type filter
    if (filterType !== 'all') {
      filtered = filtered.filter(project => 
        project.type.toLowerCase() === filterType.toLowerCase()
      );
    }

    return filtered;
  }, [allProjects, searchTerm, filterType]);

  // Paginated results
  const paginatedProjects = useMemo(() => {
    const startIndex = (currentPage - 1) * PROJECTS_PER_PAGE;
    return filteredProjects.slice(0, startIndex + PROJECTS_PER_PAGE);
  }, [filteredProjects, currentPage]);

  const hasNextPage = useMemo(() => {
    return paginatedProjects.length < filteredProjects.length;
  }, [paginatedProjects.length, filteredProjects.length]);

  const fetchRealData = useCallback(async () => {
    const cacheKey = 'registry_data_cache';
    const cacheTimeKey = 'registry_data_cache_time';
    
    try {
      // Check cache first
      const cached = localStorage.getItem(cacheKey);
      const cacheTime = localStorage.getItem(cacheTimeKey);
      const now = Date.now();
      
      if (cached && cacheTime && (now - parseInt(cacheTime)) < CACHE_DURATION) {
        console.log('📦 Using cached registry data');
        const cachedData = JSON.parse(cached);
        setAllProjects(cachedData);
        setProjects(cachedData);
        return;
      }

      setIsBackgroundLoading(true);
      
      // Try local database first
      console.log('📊 Fetching from local database...');
      const { data: localData, error: localError } = await supabase
        .from('carbon_projects')
        .select('*')
        .order('registration_date', { ascending: false })
        .limit(100);

      if (localData && localData.length > 0) {
        console.log(`✅ Found ${localData.length} projects in local database`);
        const formattedProjects = localData.map(project => ({
          registry_id: project.registry_id,
          name: project.name,
          developer: project.developer,
          type: project.type,
          methodology: project.methodology,
          location: project.location,
          status: project.status,
          credits_issued: Number(project.credits_issued),
          credits_retired: Number(project.credits_retired),
          vintage_year: project.vintage_year,
          description: project.description,
          registration_date: project.registration_date,
          verification_date: project.verification_date
        }));
        
        // Merge with instant data and deduplicate
        const mergedData = mergeAndDeduplicateProjects(INSTANT_MOCK_DATA, formattedProjects);
        setAllProjects(mergedData);
        setProjects(mergedData);
        
        // Cache the result
        localStorage.setItem(cacheKey, JSON.stringify(mergedData));
        localStorage.setItem(cacheTimeKey, now.toString());
        
        return;
      }

      // Fallback to external fetch in background
      console.log('🌐 Fetching from external registries in background...');
      try {
        const { data: externalData, error: externalError } = await supabase.functions
          .invoke('fetch-registry-data', {
            body: { action: 'fetch_all' }
          });

        if (externalData?.success && externalData?.data) {
          console.log(`✅ Found ${externalData.data.length} projects from external registries`);
          const mergedData = mergeAndDeduplicateProjects(INSTANT_MOCK_DATA, externalData.data);
          setAllProjects(mergedData);
          setProjects(mergedData);
          
          // Cache and store locally in background
          localStorage.setItem(cacheKey, JSON.stringify(mergedData));
          localStorage.setItem(cacheTimeKey, now.toString());
          
          // Store in database for next time (background operation)
          if (externalData.data.length > 0) {
            supabase
              .from('carbon_projects')
              .upsert(
                externalData.data.map((project: RegistryProject) => ({
                  registry_id: project.registry_id,
                  name: project.name,
                  developer: project.developer,
                  type: project.type,
                  methodology: project.methodology,
                  location: project.location,
                  status: project.status,
                  credits_issued: project.credits_issued,
                  credits_retired: project.credits_retired,
                  vintage_year: project.vintage_year,
                  description: project.description,
                  registration_date: project.registration_date,
                  verification_date: project.verification_date,
                  user_id: '00000000-0000-0000-0000-000000000000'
                })),
                { onConflict: 'registry_id' }
              )
              .then(({ error }) => {
                if (!error) {
                  console.log('✅ Successfully cached external data locally');
                }
              });
          }
        }
      } catch (funcError) {
        console.warn('⚠️ External fetch failed, using mock data:', funcError);
      }

    } catch (err: any) {
      console.warn('⚠️ Background fetch failed:', err);
      setError('Background data sync failed, using cached data');
    } finally {
      setIsBackgroundLoading(false);
    }
  }, []);

  const mergeAndDeduplicateProjects = (instantData: RegistryProject[], realData: RegistryProject[]): RegistryProject[] => {
    const seen = new Set();
    const merged: RegistryProject[] = [];
    
    // Add real data first (priority)
    realData.forEach(project => {
      if (!seen.has(project.registry_id)) {
        seen.add(project.registry_id);
        merged.push(project);
      }
    });
    
    // Add instant data if not already present
    instantData.forEach(project => {
      if (!seen.has(project.registry_id)) {
        seen.add(project.registry_id);
        merged.push(project);
      }
    });
    
    return merged;
  };

  const refetch = useCallback(async () => {
    // Clear cache and refetch
    localStorage.removeItem('registry_data_cache');
    localStorage.removeItem('registry_data_cache_time');
    setCurrentPage(1);
    await fetchRealData();
    
    toast({
      title: "Registry Updated",
      description: "Latest data has been fetched from registries.",
    });
  }, [fetchRealData, toast]);

  const loadMore = useCallback(() => {
    if (hasNextPage && !isBackgroundLoading) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage, isBackgroundLoading]);

  const search = useCallback((term: string) => {
    setSearchTerm(term);
    setCurrentPage(1);
  }, []);

  const filter = useCallback((type: string) => {
    setFilterType(type);
    setCurrentPage(1);
  }, []);

  return {
    projects: paginatedProjects,
    allProjects: filteredProjects,
    isInitialLoading,
    isBackgroundLoading,
    error,
    hasNextPage,
    currentPage,
    totalCount: filteredProjects.length,
    searchTerm,
    filterType,
    refetch,
    loadMore,
    search,
    filter
  };
};